import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { getBackend } from '../backend';
import { SET_NVR_TIMEZONE } from '../actions/types';
import { ListRow, SetupListPanel, SetupDrill, confirmDiscardIfDirty, useReportDirty, formsEqual, isMasterRole, Field, timezoneSelectOptions, siteAdminCoverage, serverErrorText, useAutoSelectFirstInLandscape, usePersistedSelection, useSaveSelection, setupPersistedSelection } from './setupCommon';
import { fieldAccess } from './setupAccess';   // the single source: '<menu>.<field>' → access; unknown → master-edit/local-readonly
import { askConfirm } from './confirmService';

/**
 * CloudSiteSetup — the customer ▸ site tier (unified-viewer-setup-design.md §4.2; ported from
 * isite admin/forms/site/SiteForm.js into our Setup aesthetic).
 *
 * A site (DB "project", JSCustomerProject) belongs to one customer and owns the timezone every
 * device timestamp is converted to. CREATE / DELETE and re-parenting (Customer) + Data Plan are
 * MASTER-only (fleet/billing = Dividia). A non-master ADMIN who administers a device in the site may
 * edit its Name + Timezone (canEditSite) — enforced server-side by modifyOrCreateProject. File
 * Directory + Domain mirror the parent customer and are shown read-only (isite treats them as
 * customer-derived). Fields: Name, Customer, Timezone (required), Data Plan (GB), Report Errors.
 *
 * LAYOUT — shared mobile drill-down (SetupDrill), the locked template.
 */

// Timezone dropdown options come from the shared setupCommon.timezoneSelectOptions — the SAME list the
// device global-timezone picker uses (Config → Global), so the two selectors never diverge.

// cloudapi modifyOrCreateProject rejects a site name longer than this (admin.js) — cap the input to match.
const SITE_NAME_MAX = 65;

const blankSite = (customerId) => ({
  id: null, name: '', dir: '', domain: '', customerId: customerId || '',
  dataPlan: '0', timezone: '', reportErrors: false,
});

const siteToForm = (s) => ({
  id: s.bID,
  name: s.sName || '',
  dir: s.sDir || '',
  domain: s.url || '',
  customerId: s.bCustomerID,
  dataPlan: s.bDataPlanGB != null ? String(s.bDataPlanGB) : '0',
  timezone: s.sTimezone || '',
  reportErrors: s.fReportErrors === 1 || s.fReportErrors === true,
});

const CloudSiteSetup = (props) => {
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [pristine, setPristine] = useState(null); // saved/loaded snapshot; dirty = form differs from it
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('');

  const dispatch = props.dispatch;   // connect injects dispatch (no mapDispatchToProps); undefined in raw-component tests
  const isMaster = isMasterRole(props.bRole);
  const fAdmin = props.fAdmin === 1 || props.fAdmin === true;
  // Creating/deleting a site and re-parenting it (customer / data plan) is fleet provisioning —
  // master-only. A non-master ADMIN with access to this site may edit its name + timezone: those
  // fields open via the single-source access map (site.name / site.timezone local:'edit'), and the
  // backend enforces both the field limit and the site-admin authority. (David: admin + site access
  // can admin sites; org/billing stays Dividia.)
  const canManage = isMaster;              // New / Delete — master-only
  // Per-site (review #86bc63u9g F1), mirroring cloudapi modifyOrCreateProject: a non-master may edit a
  // site only when ADMIN on ≥1 of its devices (callerAdminsSite), and change its TIMEZONE only when ADMIN
  // on EVERY device of the site (the zone is pushed to each device's config). The coarse fAdmin ("ADMIN
  // somewhere") is only the fallback for a backend without the per-device map. Coverage is computed over
  // the devices this login can see; a site device hidden from the user still makes the server refuse the
  // timezone change, and that refusal's own text is shown (F3).
  const backend = getBackend();
  const deviceRights = backend && typeof backend.getDeviceRights === 'function' ? backend.getDeviceRights() : null;
  const siteDeviceIds = form && form.id && backend && typeof backend.getProjectDeviceIds === 'function'
    ? backend.getProjectDeviceIds(form.id) : [];
  const coverage = isMaster ? { any: true, all: true }
    : (deviceRights ? siteAdminCoverage(deviceRights, siteDeviceIds, fAdmin) : { any: fAdmin, all: fAdmin });
  const canEditSite = isMaster || (!!form && !!form.id && coverage.any);   // Save an existing site's editable fields
  const canEditTimezone = isMaster || (canEditSite && coverage.all);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { customers: c, projects: p } = await getBackend().getProjects({ token: props.sSess });
      setCustomers(c); setSites(p);
      return p;
    } catch (e) {
      setError('Could not load sites. ' + (e && e.message ? e.message : ''));
      return null;
    } finally {
      setLoading(false);
    }
  }, [props.sSess]);

  useEffect(() => { load(); }, [load]);

  // A fresh new-site form: customer preselected when there's only one, dir/domain derived from it.
  const freshSite = () => {
    const cid = customers.length === 1 ? customers[0].bID : '';
    const c = customers.find((x) => x.bID === cid);
    return { ...blankSite(cid), dir: c ? c.sDir : '', domain: c ? c.sDomain : '' };
  };

  const dirty = form !== null && !formsEqual(form, pristine);
  const seed = (f) => { setForm(f); setPristine(f); setError(''); setNotice(''); };

  useReportDirty(dirty);
  useSaveSelection('sites', selectedId);
  usePersistedSelection('sites', sites, (s) => s.bID, (s) => { setSelectedId(s.bID); seed(siteToForm(s)); });
  const select = async (s) => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId(s.bID); seed(siteToForm(s));
  };
  const startNew = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId('new'); seed(freshSite());
  };
  const closeDetail = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId(null); seed(null);
  };
  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setNotice(''); };
  const revert = () => { setForm(pristine); setError(''); setNotice(''); };

  // Changing the customer re-derives the read-only File Directory + Domain from that customer
  // (isite: a site's dir/domain always mirror its parent customer's sDir/sDomain).
  const changeCustomer = (customerId) => {
    const c = customers.find((x) => x.bID === Number(customerId));
    patch({ customerId: Number(customerId), dir: c ? c.sDir : '', domain: c ? c.sDomain : '' });
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.customerId) { setError('A customer is required.'); return; }
    // A partial site-admin (not ADMIN on every site device) can't change the timezone, so the stored
    // value is sent as-is — and an EMPTY stored zone must not block a name-only edit (review round 3).
    const timezone = canEditTimezone ? form.timezone : (pristine ? pristine.timezone : form.timezone);
    if (!timezone && canEditTimezone) { setError('A timezone is required.'); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      const { ok, error } = await getBackend().saveProject(
        { ...form, timezone, dataPlan: form.dataPlan ? parseInt(form.dataPlan, 10) : 0 },
        { token: props.sSess });
      if (!ok) throw new Error(error || 'Save was rejected.');
      setNotice(form.id ? 'Site saved.' : 'Site created.');
      // Stay on the saved record (re-seeded clean) instead of deselecting to a blank pane. Find it by id
      // (edit) or by name+customer (a fresh create, whose new id the save response doesn't return). (David 2026-08-30)
      const fresh = await load();
      const saved = (fresh || []).find((s) => (form.id ? s.bID === form.id : (s.sName === form.name && s.bCustomerID === Number(form.customerId))));
      if (saved) {
        setSelectedId(saved.bID); seed(siteToForm(saved));
        // Reflect the saved timezone in the live header clock + playback "now" WITHOUT a re-login
        // (#86bbm5p92): refresh the cached session zone, then re-seed Redux from it. The header clock
        // reads state.utility.nvrTimezone on its next tick, so this updates on screen immediately.
        getBackend().updateProjectTimezone(saved.bID, saved.sTimezone);
        if (dispatch) dispatch({ type: SET_NVR_TIMEZONE, nvrTimezone: getBackend().getPrimaryTimezone(), nvrDateFormat: '' });
      }
      else { setSelectedId(null); setForm(null); setPristine(null); }
    } catch (e) {
      setError('Could not save. ' + serverErrorText(e, e && e.message ? e.message : 'You may not have permission.'));   // server's own text first (F3)
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!form || !form.id) return;
    if (!(await askConfirm({
      title: 'Delete site?',
      message: `Delete site "${form.name}"? Users will lose access to it and its devices will be unassigned. This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    setBusy(true); setError('');
    try {
      const { ok } = await getBackend().deleteProject(form.id, { token: props.sSess });
      if (!ok) throw new Error('Delete was rejected.');
      await load(); setSelectedId(null); setForm(null); setPristine(null);
    } catch (e) {
      setError('Could not delete. ' + (e && e.message ? e.message : ''));
    } finally { setBusy(false); }
  };

  const filtered = sites.filter((s) =>
    !filter ||
    (s.sName || '').toLowerCase().includes(filter.toLowerCase()) ||
    (s.sCustomerName || '').toLowerCase().includes(filter.toLowerCase()));
  // mobile-landscape split shows list + detail together → auto-select the first row on first load
  // so the detail pane isn't empty (landscape-only; portrait keeps the list-first drill).
  useAutoSelectFirstInLandscape(filtered, selectedId !== null || setupPersistedSelection('sites') != null, select);

  const detailOpen = form !== null;
  const detailTitle = selectedId === 'new'
    ? 'New Site'
    : ((sites.find((s) => s.bID === selectedId) || {}).sName || 'Site');

  return (
    <SetupDrill
      detailOpen={detailOpen}
      backLabel="Sites"
      wideList
      title={detailTitle}
      onBack={closeDetail}
      list={(
        <SetupListPanel
          searchFirst
          newLabel="+ New"
          onNew={canManage ? startNew : null}
          filter={filter}
          setFilter={setFilter}
          filterPlaceholder="Filter sites…"
          loading={loading}
          empty={!filtered.length}
          emptyText="No sites."
        >
          {filtered.map((s) => (
            <ListRow
              key={s.bID}
              active={selectedId === s.bID}
              onClick={() => select(s)}
              name={s.sName}
              sub={s.sCustomerName}
            />
          ))}
        </SetupListPanel>
      )}
      actions={detailOpen && canEditSite ? (
        <>
          {form.id && canManage ? (
            <button className="setup-btn--danger" disabled={busy} onClick={del}>Delete</button>
          ) : null}
          <div className="setup-actions__spacer" />
          {dirty ? (
            <button className="setup-btn--secondary" disabled={busy} onClick={revert}>Revert</button>
          ) : null}
          <button
            className={'setup-btn--save' + (dirty ? '' : ' setup-btn--save-clean')}
            disabled={busy || !dirty} onClick={save}>
            {busy ? 'Saving…' : (form.id ? 'Save' : 'Create Site')}
          </button>
        </>
      ) : null}
    >
      {detailOpen ? (
        <>
          {/* 2-column at landscape (Name/Customer | Timezone/Data Plan) to use the width + avoid a
              scroll; stacks to one column in portrait (the wrapper is a plain block there). */}
          {/* Every input is the ONE Field — type picks the widget, access (single source) picks the gate. */}
          <div className="setup-form-grid2">
            <Field type="string" label="Name" value={form.name} access={fieldAccess('site.name')} isMaster={isMaster}
              onChange={(v) => patch({ name: v })} placeholder="Site name" maxLength={SITE_NAME_MAX} disabled={!isMaster && !canEditSite} />
            <Field type="enum" label="Customer" value={form.customerId || ''} access={fieldAccess('site.customer')} isMaster={isMaster}
              disabled={customers.length <= 1}
              options={[{ value: '', label: '— select —' }, ...customers.map((c) => ({ value: String(c.bID), label: c.sName }))]}
              onChange={(v) => changeCustomer(v)} />
            <Field type="enum" label="Timezone" value={form.timezone} access={fieldAccess('site.timezone')} isMaster={isMaster}
              options={timezoneSelectOptions(form.timezone)} disabled={!canEditTimezone}
              hint={canEditSite && !canEditTimezone ? 'Changing the timezone requires administrator rights on every device in this site.' : undefined}
              onChange={(v) => patch({ timezone: v })} />
            <Field type="number" label="Data Plan (GB)" value={form.dataPlan} access={fieldAccess('site.dataPlan')} isMaster={isMaster}
              min="0" onChange={(v) => patch({ dataPlan: v })} />
          </div>
          <Field type="bool" label="Email error reports for this site" value={form.reportErrors}
            access={fieldAccess('site.reportErrors')} isMaster={isMaster} onChange={(v) => patch({ reportErrors: v })} />

          {/* Customer-derived, shown for reference (set on the Customer). Read-only via the single source. */}
          <div className="setup-derived">
            <Field type="string" label="File Directory" value={form.dir || '—'} access={fieldAccess('site.fileDir')} isMaster={isMaster} />
            <Field type="string" label="Domain" value={form.domain || '—'} access={fieldAccess('site.domain')} isMaster={isMaster} />
          </div>

          {!canManage && !canEditSite ? (
            <div className="setup-hint setup-hint--spaced">You don&rsquo;t administer any device in this site, so it is read-only.</div>
          ) : !canManage ? (
            <div className="setup-hint setup-hint--spaced">You can edit this site&rsquo;s name and timezone. Customer assignment and data plan are managed by Dividia.</div>
          ) : null}

          {error ? <div className="setup-error">{error}</div> : null}
          {notice ? <div className="setup-notice">{notice}</div> : null}
        </>
      ) : null}
    </SetupDrill>
  );
};

export { CloudSiteSetup }; // bare, for unit tests (pass sSess/bRole as props)

const mapStateToProps = (state) => ({
  sSess: state.auth.sSess,
  bRole: state.user.bRole,   // 0/1 master bit (1 = Dividia master → create/delete/reassign sites)
  fAdmin: state.user.fAdmin, // ADMIN right — a non-master site-admin edits name/timezone via this
});

export default connect(mapStateToProps)(CloudSiteSetup);
